import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  Typography,
  Alert,
  Button,
} from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import {
  requestService,
  WillOpenCardKey,
} from "src/api/request/requestService";
import { axiosInstance } from "src/api/config/axiosInstance";
import { useTranslate } from "src/locales";

interface ConsultationModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (data: {
    arrivalType: string;
    department: string;
    doctor: string;
    refDoctor: string;
    isRefDoctor: boolean;
    callTime: string;
    notes: string;
  }) => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const ConsultationModal: React.FC<ConsultationModalProps> = ({
  open,
  onClose,
  onSave,
  additionalInfoKey,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));

  const [arrivalType, setArrivalType] = useState("");
  const [department, setDepartment] = useState("");
  const [doctor, setDoctor] = useState("");
  const [refDoctor, setRefDoctor] = useState("");
  const [isRefDoctor, setIsRefDoctor] = useState(false);
  const [notes, setNotes] = useState("");
  const [callTime, setCallTime] = useState<Dayjs | null>(dayjs());

  const [arrivalOptions, setArrivalOptions] = useState<{ label: string; value: string }[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>(
    []
  );
  const [doctorOptions, setDoctorOptions] = useState<{ label: string; value: string }[]>([]);

  const [loadingArrival, setLoadingArrival] = useState(false);
  const [loadingDept, setLoadingDept] = useState(false);
  const [loadingDoctor, setLoadingDoctor] = useState(false);
  const [error, setError] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);

  // Selected labels for display
  const selectedArrivalTypeLabel = useMemo(() => {
    const found = arrivalOptions.find((opt) => opt.value === arrivalType);
    return found?.label || "";
  }, [arrivalType, arrivalOptions]);

  const selectedDepartmentLabel = useMemo(() => {
    const found = departmentOptions.find((opt) => opt.value === department);
    return found?.label || "";
  }, [department, departmentOptions]);

  const selectedDoctorLabel = useMemo(() => {
    const found = doctorOptions.find((opt) => opt.value === doctor);
    return found?.label || "";
  }, [doctor, doctorOptions]);

  const selectedRefDoctorLabel = useMemo(() => {
    const found = doctorOptions.find((opt) => opt.value === refDoctor);
    return found?.label || "";
  }, [refDoctor, doctorOptions]);

  const handleSave = () => {
    const required =
      arrivalType &&
      department &&
      doctor &&
      callTime &&
      notes.trim().length > 0 &&
      (!isRefDoctor || refDoctor);
    if (!required) {
      setError(
        t("patient-request.consultation.errors.required")
      );
      return;
    }
    setError("");
    onSave?.({
      arrivalType,
      department,
      doctor,
      refDoctor,
      isRefDoctor,
      callTime: callTime?.toISOString() || "",
      notes,
    });
    if (!onSave) {
      onClose();
    }
  };

  useEffect(() => {
    if (open) {
      setArrivalType("");
      setDepartment("");
      setDoctor("");
      setRefDoctor("");
      setIsRefDoctor(false);
      setNotes("");
      setCallTime(dayjs());
      setError("");
    }
  }, [open]);

  // Modal açılınca hasta tiplerini getir
  useEffect(() => {
    if (!open) return;

    const fetchPatientTypes = async () => {
      try {
        setLoadingArrival(true);
        // Hasta tiplerini getir: reference/api/Consultation/patient-types
        const res = await axiosInstance.get("reference/api/Consultation/patient-types");
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setArrivalOptions(
            list.map((item: any) => ({
              label: item.name || "",
              value: String(item.id),
            }))
          );
          // Varsayılan olarak Poliklinik seçili gelsin; yoksa ilk kayıt seçilsin (sonradan değiştirilebilir)
          if (!arrivalType) {
            const defaultArrival =
              list.find(
                (item: any) =>
                  typeof item.name === "string" &&
                  item.name.toLowerCase().includes("poliklinik")
              ) ?? list[0];
            if (defaultArrival?.id) {
              setArrivalType(String(defaultArrival.id));
            }
          }
        } else {
          setArrivalOptions([]);
        }
      } catch (err) {
        console.error("patient types load error", err);
        setArrivalOptions([]);
      } finally {
        setLoadingArrival(false);
      }
    };
    fetchPatientTypes();
  }, [open]);

  // Hasta tipi seçilince departmanları getir
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!arrivalType || !selectedOfficeId) {
        setDepartmentOptions([]);
        setDepartment(""); // Departman seçimini sıfırla
        setDoctor(""); // Doktor seçimini de sıfırla
        setDoctorOptions([]); // Doktor listesini temizle
        return;
      }
      try {
        setLoadingDept(true);
        // Departmanları getir: reference/api/Consultation/departments
        const res = await axiosInstance.post("reference/api/Consultation/departments", {
          dtid: Number(arrivalType), // Department Type ID (patient-types'tan dönen id)
          offid: Number(selectedOfficeId), // Office ID
        });
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setDepartmentOptions(
            list.map((d: any) => ({
              label: d.name || "",
              value: String(d.id),
            }))
          );
        } else {
          setDepartmentOptions([]);
        }
      } catch (err) {
        console.error("departments load error", err);
        setDepartmentOptions([]);
      } finally {
        setLoadingDept(false);
      }
    };
    fetchDepartments();
  }, [arrivalType, selectedOfficeId]);

  // Departman seçilince doktorları getir
  useEffect(() => {
    const fetchDoctors = async () => {
      if (!department || !selectedOfficeId) {
        setDoctorOptions([]);
        setDoctor(""); // Doktor seçimini sıfırla
        setRefDoctor(""); // Referans doktor seçimini de sıfırla
        return;
      }
      try {
        setLoadingDoctor(true);
        // Müsait doktorları getir: reference/api/Consultation/available-doctors
        const res = await axiosInstance.post("reference/api/Consultation/available-doctors", {
          deptid: Number(department), // Department ID
          offid: Number(selectedOfficeId), // Office ID
          date: new Date().toISOString(), // Opsiyonel: Tarih (varsayılan: şu anki zaman)
        });
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setDoctorOptions(
            list.map((d: any) => ({
              label: `${d.drtitle || ""} ${d.name || ""} ${d.surname || ""}`.trim(),
              value: String(d.id),
            }))
          );
        } else {
          setDoctorOptions([]);
        }
      } catch (err) {
        console.error("available doctors load error", err);
        setDoctorOptions([]);
      } finally {
        setLoadingDoctor(false);
      }
    };
    fetchDoctors();
  }, [department, selectedOfficeId]);

  const disablePast = (date: Dayjs) => date.isBefore(dayjs().startOf("day"));

  // WillOpenCard KEY bazlı ek bilgi formunu merkezi servisten yükle
  useEffect(() => {
    if (!open || !additionalInfoKey || !additionalInfoParams) return;

    const loadAdditionalInfo = async () => {
      try {
        const res = await requestService.getAdditionalInfoForKey({
          key: additionalInfoKey,
          serviceId: additionalInfoParams.serviceId,
          requestDetailId: additionalInfoParams.requestDetailId,
          ptId: additionalInfoParams.ptId,
          officeId: additionalInfoParams.officeId,
        });
        setAdditionalInfo(res.data);
         // TODO: Dönen ek bilgi formuna göre alanları pre-fill etmek için kullanılabilir.
        if (res) {
          setAdditionalInfo(res);
        }
      } catch {
        // Hata durumunda mevcut manuel seçim akışı devam etsin.
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoKey, additionalInfoParams]);

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {t("patient-request.consultation.title")}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.consultation.arrival-type")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
              <ByzSelectListPopupInput
                fullWidth
                size="small"
                label={t("patient-request.consultation.arrival-type")}
                value={selectedArrivalTypeLabel}
                selectedId={arrivalType}
                staticData={arrivalOptions.map((opt) => ({
                  id: opt.value,
                  NAME: opt.label,
                }))}
                displayField="NAME"
                disabled={loadingArrival || arrivalOptions.length === 0}
                onChange={(rows) => {
                  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                  const first = list[0];
                  if (first) {
                    setArrivalType(String(first.id));
                  } else {
                    setArrivalType("");
                  }
                  // Üst satır (Hasta Tipi) değişince alt satırları temizle
                  setDepartment("");
                  setDoctor("");
                  setRefDoctor("");
                  setDepartmentOptions([]);
                  setDoctorOptions([]);
                }}
                dialogMaxHeight="60vh"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.consultation.department")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
              <ByzSelectListPopupInput
                fullWidth
                size="small"
                label={t("patient-request.consultation.department")}
                value={selectedDepartmentLabel}
                selectedId={department}
                staticData={departmentOptions.map((opt) => ({
                  id: opt.value,
                  NAME: opt.label,
                }))}
                displayField="NAME"
                disabled={
                  !arrivalType ||
                  loadingDept ||
                  departmentOptions.length === 0
                }
                onChange={(rows) => {
                  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                  const first = list[0];
                  if (first) {
                    setDepartment(String(first.id));
                  } else {
                    setDepartment("");
                  }
                  // Üst satır (Bölüm) değişince alt satırları temizle
                  setDoctor("");
                  setRefDoctor("");
                  setDoctorOptions([]);
                }}
                dialogMaxHeight="60vh"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.consultation.doctor")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
              <ByzSelectListPopupInput
                fullWidth
                size="small"
                label={t("patient-request.consultation.doctor")}
                value={selectedDoctorLabel}
                selectedId={doctor}
                staticData={doctorOptions.map((opt) => ({
                  id: opt.value,
                  NAME: opt.label,
                }))}
                displayField="NAME"
                disabled={
                  !department ||
                  loadingDoctor ||
                  doctorOptions.length === 0
                }
                onChange={(rows) => {
                  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                  const first = list[0];
                  if (first) {
                    setDoctor(String(first.id));
                  } else {
                    setDoctor("");
                  }
                  // Üst satır (Doktor) değişince alt satır (Referans Doktor) temizlensin
                  setRefDoctor("");
                }}
                dialogMaxHeight="60vh"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              Referans Doktor
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Box sx={{ width: 320 }}>
                <ByzSelectListPopupInput
                  fullWidth
                  size="small"
                  label={t("patient-request.consultation.reference-doctor")}
                  value={selectedRefDoctorLabel}
                  selectedId={refDoctor}
                  staticData={doctorOptions.map((opt) => ({
                    id: opt.value,
                    NAME: opt.label,
                  }))}
                  displayField="NAME"
                  disabled={
                    !isRefDoctor ||
                    !department ||
                    loadingDoctor ||
                    doctorOptions.length === 0
                  }
                  onChange={(rows) => {
                    const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                    const first = list[0];
                    if (first) {
                      setRefDoctor(String(first.id));
                    } else {
                      setRefDoctor("");
                    }
                  }}
                  dialogMaxHeight="60vh"
                />
              </Box>
              <ByzMuiCheckBox
                text={t("patient-request.consultation.reference-doctor-check")}
                checked={isRefDoctor}
                onChange={(e) => {
                  setIsRefDoctor(e.target.checked);
                  if (!e.target.checked) {
                    setRefDoctor(""); // Checkbox kapatılınca referans doktor seçimini temizle
                  }
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.consultation.call-time")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
              <ByzMuiDateTimePicker
                sx={{ width: "100%" }}
                value={callTime}
                onChange={(dt) => setCallTime(dt)}
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    required: false,
                    error: false,
                  },
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.consultation.notes")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <ByzMuiTextField
                sx={{ width: 320 }}
                multiline
                minRows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("patient-request.consultation.notes-placeholder")}
                required
              />
              <Button variant="outlined" size="small" onClick={() => {}}>
                {t("patient-request.consultation.add-template")}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default ConsultationModal;
